import { useAtomValue, useSetAtom } from 'jotai';
import { ReactNode, useEffect } from 'react';
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';

import { businessLocationQuery } from '@/__generated__/businessLocationQuery.graphql';
import { locationQuery } from '@/graphql/query/survey/business';
import { locationsDataAtom } from '@/lib/stores/survey/business';
import { currentBusinessBmanTinAtom, refundHistoryIdAtom } from '@/lib/stores/survey/common';

export const LocationProvider = ({ children }: { children: ReactNode }) => {
  const [queryRef, loadQuery] = useQueryLoader<businessLocationQuery>(locationQuery);

  const refundHistoryId = useAtomValue(refundHistoryIdAtom);
  const bmanTin = useAtomValue(currentBusinessBmanTinAtom);

  useEffect(() => {
    if (refundHistoryId && bmanTin) {
      loadQuery(
        {
          refundHistoryId,
          bmanTin
        },
        { fetchPolicy: 'network-only' }
      );
    }
  }, [refundHistoryId, bmanTin, loadQuery]);

  if (!queryRef) return <></>;
  return <LocationAtomProvider queryRef={queryRef}>{children}</LocationAtomProvider>;
};

const LocationAtomProvider = ({ queryRef, children }: { queryRef: PreloadedQuery<businessLocationQuery>; children: ReactNode }) => {
  const { getSurveyBusinessAnswer, getSurveyBusinessInfo } = usePreloadedQuery<businessLocationQuery>(locationQuery, queryRef);

  const setLocationData = useSetAtom(locationsDataAtom);

  const businessInfo = getSurveyBusinessInfo?.business?.[0];
  const businessAnswer = getSurveyBusinessAnswer?.businessAnswer?.[0];

  useEffect(() => {
    if (businessAnswer && businessInfo) {
      setLocationData({
        isStartBusinessDirectly: businessAnswer?.isStartBusinessDirectly === 'yes',
        openingDate: businessInfo?.ofbDt || '',
        hometaxAddress: businessInfo?.adr || '',
        isFirstLocation: businessAnswer?.isFirstLocation || 'blank',
        locations:
          businessAnswer?.locations?.map(({ isFirstLocation, isUnknownRelocationYear, year, address }) => ({
            isHometaxRegAddress: address === businessInfo?.adr,
            year: year || 0,
            address,
            isFirstLocation,
            isUnknownRelocationYear
          })) || []
      });
    }
  }, [businessAnswer, businessInfo, setLocationData]);

  return <>{children}</>;
};
